import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useBrand } from "@/hooks/useSiteContent";
import { getConsent, setConsent } from "@/lib/analytics";

export const Route = createFileRoute("/politica-de-privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — Contato Internet" },
      {
        name: "description",
        content:
          "Como a Contato Internet coleta e usa dados de navegação, cookies e informações de contato, conforme a LGPD.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const brand = useBrand();
  const [consent, setConsentState] = useState(getConsent());

  const change = (state: "granted" | "denied") => {
    setConsent(state);
    setConsentState(state);
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
      <h1 className="text-2xl font-extrabold uppercase tracking-tight text-primary sm:text-3xl">
        Política de Privacidade
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Última atualização: setembro de 2026
      </p>

      <div className="mt-8 space-y-6 text-sm leading-relaxed text-foreground">
        <section>
          <h2 className="text-base font-bold">1. Quem somos</h2>
          <p className="mt-2">
            Este site é operado pela Contato Internet. Para dúvidas sobre seus dados, fale conosco
            pelos canais de atendimento divulgados no site
            {brand.email ? ` ou pelo e-mail ${brand.email}` : ""}.
          </p>
        </section>

        <section>
          <h2 className="text-base font-bold">2. Dados que coletamos</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>
              <strong>Navegação:</strong> páginas visitadas, origem do acesso (incluindo campanhas
              de anúncios), tipo de dispositivo e navegador — via Google Analytics e Google Ads.
            </li>
            <li>
              <strong>Contato:</strong> quando você nos envia um formulário ou inicia uma conversa
              no WhatsApp/chat, tratamos os dados que você fornece (nome, telefone, e-mail,
              mensagem) para atendê-lo.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-base font-bold">3. Cookies e tecnologias de medição</h2>
          <p className="mt-2">
            Usamos cookies para medir a audiência do site e avaliar a eficácia de nossos anúncios.
            Esses cookies só são ativados <strong>após o seu consentimento</strong>. Você pode
            alterar sua escolha a qualquer momento abaixo.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
            <span className="text-sm">
              Estado atual:{" "}
              <strong>
                {consent === "granted"
                  ? "cookies de medição aceitos"
                  : consent === "denied"
                    ? "cookies de medição recusados"
                    : "sem escolha registrada"}
              </strong>
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => change("denied")}
                className="rounded-lg border border-input px-3 py-1.5 text-sm font-semibold transition hover:bg-accent"
              >
                Recusar
              </button>
              <button
                type="button"
                onClick={() => change("granted")}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-extrabold uppercase tracking-wide text-primary-foreground transition hover:brightness-110"
              >
                Aceitar
              </button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-bold">4. Compartilhamento</h2>
          <p className="mt-2">
            Dados de navegação são processados pelo Google (Analytics e Ads) conforme as políticas
            da plataforma. Não vendemos seus dados.
          </p>
        </section>

        <section>
          <h2 className="text-base font-bold">5. Seus direitos (LGPD)</h2>
          <p className="mt-2">
            Você pode solicitar acesso, correção ou exclusão dos seus dados, bem como revogar o
            consentimento, pelos canais de atendimento da Contato Internet.
          </p>
        </section>
      </div>
    </div>
  );
}
